const MINUTE = 60;
const HOUR = 3600;
const DAY = 86400;
const WEEK = 604800;
const MONTH = 2592000; // 30 days
const YEAR = 31536000; // 365 days

/**
 * Returns a human-readable relative time string like "2 hours ago".
 * Accepts Date, ISO string, or null/undefined (returns '—').
 */
export function formatRelativeTime(date: Date | string | null | undefined): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '—';
  const now = Date.now();
  const diffSec = Math.round((now - d.getTime()) / 1000);

  if (diffSec < 0) return 'just now';
  if (diffSec < MINUTE) return 'just now';
  if (diffSec < HOUR) {
    const m = Math.floor(diffSec / MINUTE);
    return m === 1 ? '1 minute ago' : `${m} minutes ago`;
  }
  if (diffSec < DAY) {
    const h = Math.floor(diffSec / HOUR);
    return h === 1 ? '1 hour ago' : `${h} hours ago`;
  }
  if (diffSec < WEEK) {
    const days = Math.floor(diffSec / DAY);
    return days === 1 ? 'yesterday' : `${days} days ago`;
  }
  if (diffSec < MONTH) {
    const weeks = Math.floor(diffSec / WEEK);
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  }
  if (diffSec < YEAR) {
    const months = Math.floor(diffSec / MONTH);
    return months === 1 ? '1 month ago' : `${months} months ago`;
  }
  const years = Math.floor(diffSec / YEAR);
  return years === 1 ? '1 year ago' : `${years} years ago`;
}

/**
 * Returns the full formatted date string for use in tooltips.
 */
export function formatFullDateTime(date: Date | string | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}